/**
 * Calendar days and times in Europe/Skopje, whatever the browser's own time zone is. A "day" is a
 * 'YYYY-MM-DD' string (what the API's ?date= / ?from= / ?to= take); a month is 'YYYY-MM'. Display
 * formats follow docs/i18n/glossary-mk.md: dd.MM.yyyy, 24-hour HH:mm, „денес“ / „вчера“.
 */
export const SKOPJE_TZ = 'Europe/Skopje';

export type Locale = 'mk' | 'en';

const MONTHS: Record<Locale, readonly string[]> = {
  mk: [
    'јануари',
    'февруари',
    'март',
    'април',
    'мај',
    'јуни',
    'јули',
    'август',
    'септември',
    'октомври',
    'ноември',
    'декември',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

const RELATIVE: Record<Locale, { today: string; yesterday: string }> = {
  mk: { today: 'денес', yesterday: 'вчера' },
  en: { today: 'today', yesterday: 'yesterday' },
};

let partsFormatter: Intl.DateTimeFormat | null = null;

function skopjeParts(date: Date): {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
} {
  partsFormatter ??= new Intl.DateTimeFormat('en-GB', {
    timeZone: SKOPJE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });
  const out = { year: '', month: '', day: '', hour: '', minute: '' };
  for (const p of partsFormatter.formatToParts(date)) {
    if (p.type in out) out[p.type as keyof typeof out] = p.value;
  }
  return out;
}

const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTH_RE = /^(\d{4})-(\d{2})$/;

/** A real calendar day 'YYYY-MM-DD' (2026-02-30 is not one). */
export function isValidDay(day: string): boolean {
  if (!DAY_RE.test(day)) return false;
  const d = new Date(`${day}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === day;
}

export function isValidMonth(month: string): boolean {
  const m = MONTH_RE.exec(month);
  return !!m && Number(m[2]) >= 1 && Number(m[2]) <= 12;
}

/** Today in Skopje, 'YYYY-MM-DD'. */
export function skopjeToday(now: Date = new Date()): string {
  const p = skopjeParts(now);
  return `${p.year}-${p.month}-${p.day}`;
}

/** The current Skopje month, 'YYYY-MM'. */
export function skopjeMonth(now: Date = new Date()): string {
  return skopjeToday(now).slice(0, 7);
}

/** The Skopje calendar day of an instant (ISO string). */
export function skopjeDayOf(iso: string): string {
  return skopjeToday(new Date(iso));
}

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (to - from); 0 for the same day. */
export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  return Math.round((b - a) / 86_400_000);
}

export function firstOfMonth(day: string): string {
  return `${day.slice(0, 7)}-01`;
}

/** The previous month of 'YYYY-MM'. */
export function previousMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** Last day of 'YYYY-MM' as 'YYYY-MM-DD'. */
export function lastDayOfMonth(month: string): string {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m, 0)); // day 0 of the next month
  return d.toISOString().slice(0, 10);
}

/** 'YYYY-MM-DD' -> 'dd.MM.yyyy' */
export function formatDay(day: string): string {
  const m = DAY_RE.exec(day);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : day;
}

/** An instant as its Skopje date 'dd.MM.yyyy'. */
export function formatDate(iso: string): string {
  const p = skopjeParts(new Date(iso));
  return `${p.day}.${p.month}.${p.year}`;
}

/** An instant as its Skopje time 'HH:mm'. */
export function formatTime(iso: string): string {
  const p = skopjeParts(new Date(iso));
  return `${p.hour}:${p.minute}`;
}

/** '12.10.2026, 21:45', or 'денес, 21:45' / 'вчера, 21:45' for the last two days. */
export function formatDateTime(iso: string, locale: Locale, now: Date = new Date()): string {
  const day = skopjeDayOf(iso);
  const today = skopjeToday(now);
  const time = formatTime(iso);
  if (day === today) return `${RELATIVE[locale].today}, ${time}`;
  if (day === addDays(today, -1)) return `${RELATIVE[locale].yesterday}, ${time}`;
  return `${formatDate(iso)}, ${time}`;
}

/** 'YYYY-MM' -> 'октомври 2026' / 'October 2026'. */
export function formatMonth(month: string, locale: Locale): string {
  const m = MONTH_RE.exec(month);
  if (!m) return month;
  const name = MONTHS[locale][Number(m[2]) - 1];
  return name ? `${name} ${m[1]}` : month;
}

/** 'YYYY-MM-DD' -> '12 октомври 2026' / '12 October 2026'. */
export function formatLongDay(day: string, locale: Locale): string {
  const m = DAY_RE.exec(day);
  if (!m) return day;
  return `${Number(m[3])} ${formatMonth(`${m[1]}-${m[2]}`, locale)}`;
}

/** Seconds as '90 сек.' / '1 мин. 30 сек.' (mk) or '90 s' / '1 min 30 s' (en). */
export function formatSeconds(seconds: number, locale: Locale): string {
  const s = Math.round(seconds);
  const min = Math.floor(s / 60);
  const sec = s % 60;
  const u = locale === 'mk' ? { min: 'мин.', sec: 'сек.' } : { min: 'min', sec: 's' };
  if (min === 0) return `${sec} ${u.sec}`;
  return sec === 0 ? `${min} ${u.min}` : `${min} ${u.min} ${sec} ${u.sec}`;
}
